import React, { useState, useEffect, useCallback } from 'react';
import { 
    Container, 
    Typography, 
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    IconButton,
    Tooltip,
    TextField
} from '@mui/material';
import { getRequest, postRequest, deleteRequest } from "../../Fetch/request";
import Loader from "../Loader/Loader";
import { useNavigate } from 'react-router-dom';
import { ExpandMore, QuestionAnswer, Visibility, ArrowBack, ArrowForward, Sync, Close, Delete } from '@mui/icons-material';
import { getAIFeedbackSync } from "../../Utils/aiUtils";
import { useAuth } from '../../hooks/useAuth';
import { getPromptForComponent, getSchemaForComponent } from "../../Utils/aiPrompts";

const NotesPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
    const [notes, setNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [selectedNote, setSelectedNote] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

    const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
    const [userAnswers, setUserAnswers] = useState({});
    const [editingAnswer, setEditingAnswer] = useState(null);
    const [answerInputs, setAnswerInputs] = useState({});
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [isGettingAIFeedback, setIsGettingAIFeedback] = useState(false);
    const [aiAnswers, setAiAnswers] = useState({});
    const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
    const [deleteConfirmNote, setDeleteConfirmNote] = useState(null); // note to delete
    const [isDeletingNote, setIsDeletingNote] = useState(false);

    // Function to render formatted feedback with proper styling
    const renderFormattedFeedback = (content) => {
        if (typeof content !== 'string') {
            return <Typography variant="body2">{JSON.stringify(content, null, 2)}</Typography>;
        }

        // Split content by lines and process each line
        const lines = content.split('\n');
        const elements = [];
        
        lines.forEach((line, index) => {
            if (line.startsWith('## ')) {
                // Header
                const headerText = line.replace('## ', '').replace(/\*\*/g, '');
                elements.push(
                    <Typography 
                        key={index} 
                        variant="h6" 
                        sx={{ 
                            fontSize: '1rem',
                            fontWeight: 'bold', 
                            color: '#008080', 
                            mt: index > 0 ? 1.5 : 0, 
                            mb: 0.5 
                        }}
                    >
                        {headerText}
                    </Typography>
                );
            } else if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
                // Bold line
                const boldText = line.replace(/\*\*/g, '');
                elements.push(
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ fontWeight: 'bold', color: '#333', mb: 0.3, fontSize: '0.9rem' }}
                    >
                        {boldText}
                    </Typography>
                );
            } else if (line.trim() === '') {
                // Empty line - add spacing
                elements.push(<Box key={index} sx={{ height: '6px' }} />);
            } else if (line.trim()) {
                // Regular text with inline bold formatting
                const parts = line.split(/(\*\*[^*]+\*\*)/);
                const formattedParts = parts.map((part, partIndex) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                        return (
                            <Box 
                                key={partIndex} 
                                component="span" 
                                sx={{ fontWeight: 'bold', color: '#333' }}
                            >
                                {part.replace(/\*\*/g, '')}
                            </Box>
                        );
                    }
                    return part;
                });
                
                elements.push(
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ 
                            lineHeight: 1.5, 
                            color: '#555', 
                            mb: 0.3,
                            fontSize: '0.9rem',
                            whiteSpace: 'pre-wrap'
                        }}
                    >
                        {formattedParts}
                    </Typography>
                );
            }
        });

        return <Box>{elements}</Box>;
    };

    const handleCreateNote = () => {
        if (!isAuthenticated) {
            alert('Please login to create notes');
            return;
        }
        window.open('/write?preview=false&blogId=' + Date.now(), '_blank');
    };

    const handleDeleteNote = async () => {
        if (!deleteConfirmNote) return;
        try {
            setIsDeletingNote(true);
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            // Delete the note
            await deleteRequest(`${window.env.REACT_APP_URL}/blog/${deleteConfirmNote.id}`);
            // Also delete associated Q&A questions if any
            if (deleteConfirmNote.isGenerated) {
                await deleteRequest(`${window.env.REACT_APP_URL}/notes-questions/${userId}/${deleteConfirmNote.id}`).catch(() => {});
            }
            setNotes(prev => prev.filter(n => n.id !== deleteConfirmNote.id));
            setSnackbar({ open: true, message: 'Note deleted successfully', severity: 'success' });
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to delete note. Please try again.', severity: 'error' });
        } finally {
            setIsDeletingNote(false);
            setDeleteConfirmNote(null);
        }
    };

    useEffect(() => {
        if (isAuthLoading) return; // Wait until auth state is determined

        if (!isAuthenticated) {
            setIsLoading(false);
            return;
        }

        const fetchNotes = async () => {
            try {
                setIsLoading(true);
                const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
                const response = await getRequest(`${window.env.REACT_APP_URL}/blog/getAllBlogs?userId=${userId}`);
                
                // Check if response is valid and successful
                if (response && response.status === '200' && response.response !== undefined) {
                    // Success: API call succeeded, even if result is empty
                    const blogs = response.response || [];

                    const formattedNotes = blogs.map((blog) => ({
                        id: blog.blogId,
                        title: blog.title,
                        date: new Date(blog.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        }),
                        isGenerated: blog.isGenerated || false,
                    }));

                    setNotes(formattedNotes);
                } else if (response && response.response !== undefined) {
                    // Response exists but status is not 200 - try to extract notes
                    const blogs = response.response || [];
                    const formattedNotes = blogs.map((blog) => ({
                        id: blog.blogId,
                        title: blog.title,
                        date: new Date(blog.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                        }),
                        isGenerated: blog.isGenerated || false,
                    }));
                    setNotes(formattedNotes);
                } else {
                    // No valid response - set empty notes (will show empty state)
                    setNotes([]);
                }
            } catch (error) {
                // Network error or exception - set empty notes (will show empty state)
                setNotes([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotes();
    }, [isAuthenticated, isAuthLoading]);

    const handleGenerateQuestions = async (blogId) => {
        try {
            setIsGeneratingQuestions(true);
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            
            // Call the getOrGenerateNotesQuestions endpoint
            const response = await getRequest(
                `${window.env.REACT_APP_URL}/notes-questions/${userId}/${blogId}`
            );
            
            if (response && response.status === '200') {
                const isRegenerating = notes.find(note => note.id === blogId)?.isGenerated;
                setSnackbar({
                    open: true,
                    message: isRegenerating ? 'Questions regenerated successfully!' : 'Questions generated successfully!',
                    severity: 'success'
                });
                
                // Update the local state to mark this note as generated
                setNotes(prevNotes => 
                    prevNotes.map(note => 
                        note.id === blogId 
                            ? { ...note, isGenerated: true }
                            : note
                    )
                );
                
                // If the review dialog is open, refresh the questions
                if (reviewDialogOpen && selectedNote && selectedNote.id === blogId) {
                    setTimeout(() => {
                        fetchQuestions(blogId);
                    }, 1000);
                }
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to generate questions. Please try again.',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Error generating questions:', error);
            setSnackbar({
                open: true,
                message: 'Error generating questions. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsGeneratingQuestions(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const updateQuestionsWithAnswers = async (blogId, updatedQuestions) => {
        try {
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            
            // Always send the complete questions array to backend
            // This ensures all questions (with answers, AI feedback, etc.) are preserved
            const response = await postRequest(
                `${window.env.REACT_APP_URL}/notes-questions/${userId}/${blogId}`,
                { questions: updatedQuestions }
            );
            
            if (response && response.status === '200') {
                setSnackbar({
                    open: true,
                    message: 'All questions updated successfully!',
                    severity: 'success'
                });
                return true;
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to save questions. Please try again.',
                    severity: 'error'
                });
                return false;
            }
        } catch (error) {
            console.error('Error updating questions:', error);
            setSnackbar({
                open: true,
                message: 'Error saving questions. Please try again.',
                severity: 'error'
            });
            return false;
        }
    };

    const fetchQuestions = async (blogId) => {
        try {
            setIsLoadingQuestions(true);
            const userId = localStorage.getItem('userId') || (JSON.parse(localStorage.getItem('profileObject') || '{}').id) || 'slazysloth_user';
            
            // Call the getNotesQuestions endpoint
            const response = await getRequest(
                `${window.env.REACT_APP_URL}/notes-questions/get/${userId}/${blogId}`
            );
            
            if (response && response.status === '200') {
                // Parse the questions array from the response
                const questionsData = response.response?.questions || [];
                
                // Handle the question objects with question, answer, and ai_answer properties
                const processedQuestions = questionsData.map(q => {
                    if (q && typeof q === 'object' && q.question) {
                        return { 
                            question: q.question,
                            answer: q.answer && q.answer.trim() !== '' ? q.answer : null,
                            ai_answer: q.ai_answer && q.ai_answer.trim() !== '' ? q.ai_answer : null
                        };
                    }
                    return { question: 'Invalid question format', answer: null, ai_answer: null };
                });
                
                setQuestions(processedQuestions);
                
                // Initialize user answers from loaded questions
                const initialUserAnswers = {};
                processedQuestions.forEach((q, idx) => {
                    if (q.answer) {
                        initialUserAnswers[idx] = q.answer;
                    }
                });
                setUserAnswers(initialUserAnswers);
            } else {
                setQuestions([]);
            }
        } catch (error) {
            console.error('Error fetching questions:', error);
            setQuestions([]);
        } finally {
            setIsLoadingQuestions(false);
        }
    };

    const handleReviewQuestions = async (note) => {
        setSelectedNote(note);
        setReviewDialogOpen(true);
        await fetchQuestions(note.id);
    };

    const handleCloseReviewDialog = () => {
        setReviewDialogOpen(false);
        setSelectedNote(null);
        setQuestions([]);
        setUserAnswers({});
        setEditingAnswer(null);
        setAnswerInputs({});
        setCurrentQuestionIndex(0);
        setAiAnswers({});
    };

    const getAIFeedbackForAnswer = useCallback(async (questionText, userAnswer, questionIndex) => {
        const callId = `${questionIndex}-${Date.now()}`;
        
        // Prevent duplicate calls
        if (isGettingAIFeedback) {
            return;
        }

        try {
            setIsGettingAIFeedback(true);
            
            // Get centralized prompt and schema
            const systemPrompt = getPromptForComponent('NOTES', 'ANSWER_EVALUATION');
            const evaluationSchema = getSchemaForComponent('NOTES', 'ANSWER_EVALUATION');
            const userMessage = `Please evaluate this answer:\n\nQuestion: ${questionText}\n\nUser's Answer: ${userAnswer}`;
            
            const aiFeedback = await getAIFeedbackSync(
                systemPrompt, 
                userMessage,
                null, // userId
                'anthropic', // provider
                null, // model
                evaluationSchema, // schema
                {
                  maxRetries: 3,
                  onRetry: (attempt, error, delay) => {
                  }
                }
            );
            
            // Format the structured AI feedback for display
            let formattedFeedback = "";
            if (typeof aiFeedback === 'object' && aiFeedback !== null) {
                
                if (aiFeedback.feedback) {
                    formattedFeedback += `## 📝 **Overall Feedback**\n${aiFeedback.feedback}\n\n`;
                }
                
                if (aiFeedback.strengths) {
                    formattedFeedback += `## ✅ **Strengths**\n${aiFeedback.strengths}\n\n`;
                }
                
                if (aiFeedback.improvements) {
                    formattedFeedback += `## 🚀 **Areas for Improvement**\n${aiFeedback.improvements}\n\n`;
                }
                
                if (aiFeedback.correctness) {
                    const correctnessEmoji = aiFeedback.correctness === 'correct' ? '✅' : 
                                           aiFeedback.correctness === 'partially_correct' ? '⚠️' : '❌';
                    formattedFeedback += `## ${correctnessEmoji} **Assessment: ${aiFeedback.correctness.replace('_', ' ').toUpperCase()}**\n\n`;
                }
                
                if (aiFeedback.additional_resources) {
                    formattedFeedback += `## 📚 **Additional Resources**\n${aiFeedback.additional_resources}`;
                }
                
                formattedFeedback = formattedFeedback || JSON.stringify(aiFeedback, null, 2);
            } else if (typeof aiFeedback === 'string') {
                formattedFeedback = aiFeedback;
            } else {
                formattedFeedback = JSON.stringify(aiFeedback, null, 2);
            }
            
            // Update AI answers state
            setAiAnswers(prev => ({
                ...prev,
                [questionIndex]: formattedFeedback
            }));
            
            // Update the questions array with AI answer
            const updatedQuestions = questions.map((q, idx) => {
                if (idx === questionIndex) {
                    return {
                        ...q,
                        ai_answer: formattedFeedback
                    };
                }
                return q; // Preserve all original fields for other questions
            });
            
            setQuestions(updatedQuestions);
            
            // Always send complete questions array to backend
            if (selectedNote && selectedNote.id) {
                await updateQuestionsWithAnswers(selectedNote.id, updatedQuestions);
            }
            
            setSnackbar({
                open: true,
                message: 'AI feedback received!',
                severity: 'success'
            });
            
            
        } catch (error) {
            setSnackbar({
                open: true,
                message: 'Failed to get AI feedback. Please try again.',
                severity: 'error'
            });
        } finally {
            setIsGettingAIFeedback(false);
        }
    }, [isGettingAIFeedback, questions, selectedNote, setQuestions, setAiAnswers]);

    const handleEditAnswer = (questionIndex) => {
        setEditingAnswer(questionIndex);
    };

    const handleSaveAnswer = async (questionIndex) => {
        const answer = answerInputs[questionIndex] || '';
        if (answer.trim()) {
            // Update local state
            const newUserAnswers = {
                ...userAnswers,
                [questionIndex]: answer
            };
            setUserAnswers(newUserAnswers);
            
            // Update the questions array with the new answer
            const updatedQuestions = questions.map((q, idx) => {
                if (idx === questionIndex) {
                    return {
                        ...q,
                        answer: answer.trim()
                        // Preserve existing ai_answer and all other fields
                    };
                }
                return q; // Preserve all original fields for other questions
            });
            
            // Always send complete questions array to backend
            if (selectedNote && selectedNote.id) {
                const success = await updateQuestionsWithAnswers(selectedNote.id, updatedQuestions);
                if (success) {
                    // Update local questions state with the new answer
                    setQuestions(updatedQuestions);
                }
            }
        }
        setEditingAnswer(null);
        setAnswerInputs(prev => ({ ...prev, [questionIndex]: '' }));
    };

    const handleAnswerInputChange = (questionIndex, value) => {
        setAnswerInputs(prev => ({
            ...prev,
            [questionIndex]: value
        }));
    };

    const handleCancelEdit = () => {
        setEditingAnswer(null);
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePreviousQuestion = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleRetryWithAI = async (questionIndex) => {
        const userAnswer = userAnswers[questionIndex];
        if (userAnswer && userAnswer.trim()) {
            await getAIFeedbackForAnswer(questions[questionIndex].question, userAnswer, questionIndex);
        }
    };

    if (isLoading) {
        return <div style={{ marginTop: '100px' }}><Loader /></div>;
    }

    return (
        <Container maxWidth="md" sx={{ marginTop: '20px' }}>
            {/* Header Section with Feature Highlights */}
            <Box sx={{ mb: 4 }}>
                {/* Feature Highlight Banner */}
                <Box sx={{ 
                    backgroundColor: '#f0f9ff', 
                    border: '2px solid #008080', 
                    borderRadius: '12px', 
                    p: 2, 
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ 
                        position: 'absolute', 
                        top: -10, 
                        right: -10, 
                        width: 100, 
                        height: 100, 
                        backgroundColor: '#008080', 
                        borderRadius: '50%', 
                        opacity: 0.1 
                    }} />
                    <Typography variant="h6" sx={{ 
                        fontWeight: 'bold', 
                        color: '#008080', 
                        mb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        📝 Smart Notes & AI Questions
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', mb: 1.5, lineHeight: 1.5 }}>
                        Create comprehensive notes and automatically generate AI-powered questions to test your understanding!
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                backgroundColor: '#10b981', 
                                borderRadius: '50%' 
                            }} />
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                Create & Edit Notes
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                backgroundColor: '#f59e0b', 
                                borderRadius: '50%' 
                            }} />
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                AI Question Generation
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                backgroundColor: '#8b5cf6', 
                                borderRadius: '50%' 
                            }} />
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                Interactive Q&A Review
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ 
                                width: 8, 
                                height: 8, 
                                backgroundColor: '#ef4444', 
                                borderRadius: '50%' 
                            }} />
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                                AI Feedback & Evaluation
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Create Note Button */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end'
                }}>
                    <Button
                        variant="contained"
                        onClick={handleCreateNote}
                        sx={{
                            backgroundColor: '#6366f1',
                            color: 'white',
                            fontWeight: 600,
                            px: 3,
                            py: 1.25,
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontSize: '14px',
                            border: '1.5px solid #6366f1',
                            boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)',
                            '&:hover': {
                                backgroundColor: '#4f46e5',
                                borderColor: '#4f46e5',
                                boxShadow: '0 3px 8px rgba(99, 102, 241, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                            '&:active': {
                                transform: 'translateY(0px)'
                            },
                            transition: 'all 0.15s ease-in-out'
                        }}
                    >
                        Create New Note
                    </Button>
                </Box>
            </Box>
            
            <Paper elevation={0} sx={{ borderRadius: '8px', overflow: 'hidden', backgroundColor: 'transparent' }}>
                <TableContainer>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>Title</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>Date</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent' }}>Actions</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', fontSize:'14px', py:0.5, backgroundColor: 'transparent', width: '40px' }} />
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {notes.length > 0 ? (
                                    notes.map((note) => (
                                        <TableRow key={note.id} hover>
                                            {/* Title (Clickable Text) */}
                                            <TableCell
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.01em',
                                                    py: 0.5,
                                                }}
                                            >
                                                <a
                                                    href={`/rewrite?preview=true&blogId=${note.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'inherit', textDecoration: 'none', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '0.01em' }}
                                                >
                                                    {note.title}
                                                </a>
                                            </TableCell>

                                            {/* Date */}
                                            <TableCell align="right" sx={{ py: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                                                    {note.date}
                                                </Typography>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center" sx={{ py: 0.75 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    gap: 1, 
                                                    justifyContent: 'center', 
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap' 
                                                }}>
                                                     <Button
                                                         variant={note.isGenerated ? "contained" : "outlined"}
                                                         size="small"
                                                         onClick={() => handleGenerateQuestions(note.id)}
                                                         startIcon={note.isGenerated ? <Sync sx={{ fontSize: '12px' }} /> : null}
                                                         sx={{
                                                             backgroundColor: note.isGenerated ? '#10b981' : 'transparent',
                                                             borderColor: note.isGenerated ? '#10b981' : '#6366f1',
                                                             color: note.isGenerated ? 'white' : '#6366f1',
                                                             fontSize: '0.7rem',
                                                             fontWeight: 600,
                                                             py: 0.5,
                                                             px: 1.5,
                                                             minWidth: note.isGenerated ? '110px' : '100px',
                                                             textTransform: 'none',
                                                             borderRadius: '5px',
                                                             border: '1px solid',
                                                             boxShadow: note.isGenerated 
                                                                 ? '0 1px 2px rgba(16, 185, 129, 0.15)' 
                                                                 : '0 1px 2px rgba(99, 102, 241, 0.1)',
                                                             '&:hover': {
                                                                 backgroundColor: note.isGenerated ? '#059669' : '#6366f1',
                                                                 borderColor: note.isGenerated ? '#059669' : '#6366f1',
                                                                 color: 'white',
                                                                 boxShadow: note.isGenerated 
                                                                     ? '0 2px 4px rgba(16, 185, 129, 0.25)' 
                                                                     : '0 2px 4px rgba(99, 102, 241, 0.2)',
                                                                 transform: 'translateY(-0.5px)'
                                                             },
                                                             '&:active': {
                                                                 transform: 'translateY(0px)'
                                                             },
                                                             transition: 'all 0.15s ease-in-out'
                                                         }}
                                                     >
                                                         {note.isGenerated ? 'Regenerate' : 'Generate QnA'}
                                                     </Button>
                                                    
                                                    {note.isGenerated && (
                                                         <Button
                                                             variant="contained"
                                                             size="small"
                                                             onClick={() => handleReviewQuestions(note)}
                                                             startIcon={<QuestionAnswer sx={{ fontSize: '12px' }} />}
                                                             sx={{
                                                                 backgroundColor: '#f59e0b',
                                                                 color: 'white',
                                                                 fontSize: '0.7rem',
                                                                 fontWeight: 600,
                                                                 py: 0.5,
                                                                 px: 1.5,
                                                                 minWidth: '90px',
                                                                 textTransform: 'none',
                                                                 borderRadius: '5px',
                                                                 border: '1px solid #f59e0b',
                                                                 boxShadow: '0 1px 2px rgba(245, 158, 11, 0.15)',
                                                                 '&:hover': {
                                                                     backgroundColor: '#d97706',
                                                                     borderColor: '#d97706',
                                                                     boxShadow: '0 2px 4px rgba(245, 158, 11, 0.25)',
                                                                     transform: 'translateY(-0.5px)'
                                                                 },
                                                                 '&:active': {
                                                                     transform: 'translateY(0px)'
                                                                 },
                                                                 transition: 'all 0.15s ease-in-out'
                                                             }}
                                                         >
                                                             Review
                                                         </Button>
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* Delete */}
                                            <TableCell align="right" sx={{ py: 0.75 }}>
                                                <Tooltip title="Delete note">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteConfirmNote(note)}
                                                        sx={{
                                                            color: '#ccc',
                                                            '&:hover': { color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.08)' }
                                                        }}
                                                    >
                                                        <Delete sx={{ fontSize: '16px' }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                                            <Box sx={{ textAlign: 'center' }}>
                                                <Typography variant="h6" sx={{ 
                                                    color: '#666', 
                                                    mb: 2,
                                                    fontSize: '1.1rem',
                                                    fontWeight: 'bold'
                                                }}>
                                                    📝 No Notes Yet
                                                </Typography>
                                                <Typography variant="body2" sx={{ 
                                                    color: '#999', 
                                                    mb: 3,
                                                    lineHeight: 1.6
                                                }}>
                                                    Create your first note to get started with AI-powered question generation!
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    onClick={handleCreateNote}
                                                    sx={{
                                                        backgroundColor: '#6366f1',
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        px: 3,
                                                        py: 1.25,
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        fontSize: '14px',
                                                        border: '1.5px solid #6366f1',
                                                        boxShadow: '0 2px 4px rgba(99, 102, 241, 0.2)',
                                                        '&:hover': {
                                                            backgroundColor: '#4f46e5',
                                                            borderColor: '#4f46e5',
                                                            boxShadow: '0 3px 8px rgba(99, 102, 241, 0.3)',
                                                            transform: 'translateY(-1px)'
                                                        },
                                                        '&:active': {
                                                            transform: 'translateY(0px)'
                                                        },
                                                        transition: 'all 0.15s ease-in-out'
                                                    }}
                                                >
                                                    Create Your First Note
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
            
            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Review Questions Dialog */}
            <Dialog
                open={reviewDialogOpen}
                onClose={handleCloseReviewDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: '#f8f9fa', 
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <QuestionAnswer sx={{ color: '#008080' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                            Review Questions
                        </Typography>
                        {selectedNote && (
                            <Typography variant="body2" sx={{ ml: 1, color: '#666', fontStyle: 'italic' }}>
                                {selectedNote.title}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        onClick={handleCloseReviewDialog}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.1)',
                                color: '#333'
                            }
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 3 }}>
                    {isLoadingQuestions ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <Loader />
                        </Box>
                    ) : questions.length > 0 ? (
                        <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                            {/* Question Navigation */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                <Button
                                    onClick={handlePreviousQuestion}
                                    disabled={currentQuestionIndex === 0}
                                    startIcon={<ArrowBack />}
                                    variant="outlined"
                                    size="small"
                                    sx={{ borderColor: '#008080', color: '#008080' }}
                                >
                                    Previous
                                </Button>
                                
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                                    Question {currentQuestionIndex + 1} of {questions.length}
                                </Typography>
                                
                                <Button
                                    onClick={handleNextQuestion}
                                    disabled={currentQuestionIndex === questions.length - 1}
                                    endIcon={<ArrowForward />}
                                    variant="outlined"
                                    size="small"
                                    sx={{ borderColor: '#008080', color: '#008080' }}
                                >
                                    Next
                                </Button>
                            </Box>

                            {/* Current Question Display */}
                            <Box sx={{ backgroundColor: 'white', borderRadius: '12px', p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#333' }}>
                                    {questions[currentQuestionIndex].question}
                                </Typography>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                {/* Show existing answer or answer input */}
                                {editingAnswer === currentQuestionIndex ? (
                                    <Box sx={{ mt: 2 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                            placeholder="Type your answer here..."
                                            value={answerInputs[currentQuestionIndex] || ''}
                                            onChange={(e) => handleAnswerInputChange(currentQuestionIndex, e.target.value)}
                                            sx={{ mb: 2 }}
                                        />
                                                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => handleSaveAnswer(currentQuestionIndex)}
                                                        sx={{
                                                            backgroundColor: '#008080',
                                                            '&:hover': { backgroundColor: '#006666' }
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={handleCancelEdit}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Box>
                                    </Box>
                                ) : (
                                    <Box>
                                        {userAnswers[currentQuestionIndex] ? (
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', mb: 1 }}>
                                                    <strong>Your Answer:</strong>
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#333', mb: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                                                    {userAnswers[currentQuestionIndex]}
                                                </Typography>
                                                
                                                {/* AI Answer Section */}
                                                {questions[currentQuestionIndex].ai_answer ? (
                                                    <Box sx={{ mb: 3 }}>
                                                        <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', mb: 1 }}>
                                                            <strong>AI Feedback:</strong>
                                                        </Typography>
                                                        <Box sx={{ 
                                                            p: 2, 
                                                            backgroundColor: '#e8f5e8', 
                                                            borderRadius: '6px', 
                                                            border: '1px solid #c8e6c9',
                                                            '& h6': {
                                                                fontSize: '0.95rem',
                                                                fontWeight: 'bold',
                                                                color: '#008080',
                                                                marginBottom: '6px',
                                                                marginTop: '10px'
                                                            },
                                                            '& strong': {
                                                                fontWeight: 'bold',
                                                                color: '#333'
                                                            }
                                                        }}>
                                                            {renderFormattedFeedback(questions[currentQuestionIndex].ai_answer)}
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ mb: 3 }}>
                                                        <Button
                                                            onClick={() => handleRetryWithAI(currentQuestionIndex)}
                                                            disabled={isGettingAIFeedback}
                                                            startIcon={isGettingAIFeedback ? <CircularProgress size={16} /> : <Sync />}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                borderColor: '#008080',
                                                                color: '#008080',
                                                                '&:hover': {
                                                                    backgroundColor: '#008080',
                                                                    color: 'white'
                                                                }
                                                            }}
                                                        >
                                                            {isGettingAIFeedback ? 'Getting AI Feedback...' : 'Get AI Feedback'}
                                                        </Button>
                                                    </Box>
                                                )}
                                                
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleEditAnswer(currentQuestionIndex)}
                                                    sx={{
                                                        borderColor: '#008080',
                                                        color: '#008080',
                                                        '&:hover': {
                                                            backgroundColor: '#008080',
                                                            color: 'white'
                                                        }
                                                    }}
                                                >
                                                    Edit Answer
                                                </Button>
                                            </Box>
                                        ) : (
                                            <Box sx={{ 
                                                backgroundColor: '#f8f9fa', 
                                                p: 3, 
                                                borderRadius: '6px',
                                                border: '1px dashed #dee2e6',
                                                textAlign: 'center'
                                            }}>
                                                <Typography variant="body2" sx={{ color: '#6c757d', fontStyle: 'italic', mb: 2 }}>
                                                    <strong>Answer:</strong> No answer provided yet.
                                                </Typography>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => handleEditAnswer(currentQuestionIndex)}
                                                    sx={{
                                                        borderColor: '#008080',
                                                        color: '#008080',
                                                        '&:hover': {
                                                            backgroundColor: '#008080',
                                                            color: 'white'
                                                        }
                                                    }}
                                                >
                                                    Add Answer
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <QuestionAnswer sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                            <Typography variant="body1" sx={{ color: '#666', mb: 2 }}>
                                No questions found for this note.
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#999', mb: 3 }}>
                                Generate questions from the main notes list to create AI-powered questions based on your note content.
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
                    {/* Close button moved to header */}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirmNote}
                onClose={() => !isDeletingNote && setDeleteConfirmNote(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 'bold', color: '#333' }}>
                    Delete Note
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#555' }}>
                        Are you sure you want to delete <strong>"{deleteConfirmNote?.title}"</strong>?
                        {deleteConfirmNote?.isGenerated && ' This will also delete all generated Q&A questions.'}
                        {' '}This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={isDeletingNote}
                        onClick={() => setDeleteConfirmNote(null)}
                        sx={{ textTransform: 'none', borderColor: '#ccc', color: '#555' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        disabled={isDeletingNote}
                        onClick={handleDeleteNote}
                        startIcon={isDeletingNote ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <Delete sx={{ fontSize: '14px' }} />}
                        sx={{
                            backgroundColor: '#ef4444',
                            textTransform: 'none',
                            '&:hover': { backgroundColor: '#dc2626' },
                            '&:disabled': { backgroundColor: '#fca5a5' }
                        }}
                    >
                        {isDeletingNote ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Questions Generation Loading Dialog */}
            <Dialog
                open={isGeneratingQuestions}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        backgroundColor: 'white'
                    }
                }}
            >
                <DialogContent sx={{ p: 4, textAlign: 'center' }}>
                    <CircularProgress 
                        size={60} 
                        sx={{ color: '#008080', mb: 3 }} 
                    />
                    <Typography variant="h6" sx={{ color: '#333', mb: 2, fontWeight: 600 }}>
                        Generating Questions
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                        We're analyzing your note content and creating AI-powered questions. 
                        This may take a few moments, please wait...
                    </Typography>
                </DialogContent>
            </Dialog>
        </Container>
    );
};

export default NotesPage;
